/**
 * The comms babble under each panel line (M7 plan S31; audio-direction §5): the speaker's garbled radio voice
 * (`src/audio/comms`, `say`), one babble line per text line, in order, with `lineGapS` between them. A new node or
 * closing the panel cuts the rest of the queue (a line already on air finishes). UI events only.
 */
import { logger } from '../../core/logger'
import { hashSeed } from '../../ui/fleet/models/portrait'

/** The audio feature's comms (`audioFeature.comms`), null before the first gesture or without audio. */
export interface BabbleVoice {
  say(seed: number, stress: number, lineSeed?: number): Promise<{ utterance: { seconds: number } }>
}

export interface Babble {
  /** Babbles `lines` lines as `voice` (cancels any queued lines first). `key` seeds the "words". */
  play(voice: number, key: string, lines: number): void
  /** Cancels queued lines. */
  stop(): void
}

export interface BabbleDeps {
  /** The live comms system, read at each line (it appears after the first gesture). */
  comms: () => BabbleVoice | null
  stress: number
  gapS: number
  /** setTimeout (a fake in tests). */
  wait?: (fn: () => void, ms: number) => void
}

export function createBabble(deps: BabbleDeps): Babble {
  const wait = deps.wait ?? ((fn: () => void, ms: number): void => void setTimeout(fn, ms))
  let run = 0

  function speak(token: number, voice: number, base: number, i: number, lines: number): void {
    if (token !== run || i >= lines) return
    const comms = deps.comms()
    if (comms === null) return
    comms
      .say(voice, deps.stress, base + i)
      .then((line) => wait(() => speak(token, voice, base, i + 1, lines), (line.utterance.seconds + deps.gapS) * 1000))
      .catch((err: unknown) => logger.warn('comms panel: babble line failed', { err: String(err) }))
  }

  return {
    play(voice: number, key: string, lines: number): void {
      run++
      speak(run, voice, hashSeed(key), 0, lines)
    },
    stop(): void {
      run++
    },
  }
}
